import { cn } from "@/core/utils";
import { toneTextClass, toneBadgeClass } from "../utils/tone";
import type { TableBlock, TableCell } from "../schemas/blocks";

/**
 * Renderer do block `table` (ST-4 — Figma Estratégia): tabela comparativa com badges.
 *
 * Uma célula é `string` (texto puro) OU objeto com:
 *  - `text_tone`: colore o TEXTO da célula (coluna R$/m²);
 *  - `badge`: renderiza uma pill colorida (coluna Desvio).
 * A cor SEMPRE vem do backend (`text_tone`/`badge`) — o front nunca decide verde/vermelho.
 */
const alignClass = (align?: "left" | "right" | "center") =>
  align === "right"
    ? "text-right"
    : align === "center"
      ? "text-center"
      : "text-left";

function Cell({ cell }: { cell: TableCell }) {
  if (typeof cell === "string") {
    return <>{cell}</>;
  }
  if (cell.badge) {
    return (
      <span
        className={cn(
          "inline-block rounded-full px-2 py-0.5 text-xs font-semibold",
          toneBadgeClass(cell.badge),
        )}
      >
        {cell.text}
      </span>
    );
  }
  return (
    <span className="inline-flex flex-col">
      <span className={cn("font-medium", toneTextClass(cell.text_tone))}>
        {cell.text}
      </span>
      {cell.subtext && (
        <span className="text-xs text-darkgray-400">{cell.subtext}</span>
      )}
    </span>
  );
}

export function TableBlockView({ block }: { block: TableBlock }) {
  if (!block.columns.length || !block.rows.length) return null;
  return (
    // Título fixo; só a tabela rola horizontalmente no chat estreito (células não quebram).
    <div className="my-2 w-full overflow-hidden rounded-xl border border-lightgray-300">
      {block.title && (
        <div className="border-b border-lightgray-300 bg-white px-3 py-2 text-sm font-semibold text-darkgray-900">
          {block.title}
        </div>
      )}
      <div className="w-full overflow-x-auto">
        <table className="w-full border-collapse text-sm bg-white">
          <thead>
            <tr className="border-b border-lightgray-300 bg-lightgray-100">
              {block.columns.map((col) => (
                <th
                  key={col.key}
                  style={col.width != null ? { width: col.width } : undefined}
                  className={cn(
                    "whitespace-nowrap px-3 py-2 text-xs font-semibold text-darkgray-600",
                    alignClass(col.align),
                  )}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className={cn(
                  rowIndex < block.rows.length - 1 &&
                    "border-b border-lightgray-300",
                )}
              >
                {block.columns.map((col) => (
                  <td
                    key={col.key}
                    className={cn(
                      "whitespace-nowrap px-3 py-2 align-top text-darkgray-700",
                      alignClass(col.align),
                    )}
                  >
                    <Cell cell={row[col.key] ?? ""} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
